import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { jobsApi, usersApi, API_URL } from '../services/api';
import { StateBadge } from '../components/ui/StateBadge';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { CategoryIcon } from '../components/ui/CategoryIcon';
import { MaterialIcon } from '../components/ui/MaterialIcon';
import { IconButton } from '../components/obros/IconButton';
import { formatRelativeTime } from '../utils/helpers';
import { useAuthStore } from '../stores/authStore';

const storageUrl = (path: string) => `${API_URL.replace(/\/api\/?$/, '')}/storage/${path}`;

interface JobDetailData {
  job: any;
  client: any;
  stats: { proposals_count: number; interested_freelancers: number };
  reviews: any[];
  other_jobs: any[];
}

// Calca ScreenJobDetail de screens-job.jsx (Claude Design)
export const JobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [data, setData] = useState<JobDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    jobsApi
      .get(Number(id))
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!isAuthenticated) return;
    usersApi.myFavorites({ per_page: 100 }).then(({ data: favs }) => {
      const list = favs.data ?? favs;
      setIsFavorite(list.some((f: any) => f.favorable_type === 'App\\Models\\Job' && f.favorable_id === Number(id)));
    });
  }, [id, isAuthenticated]);

  const handleToggleFavorite = async () => {
    const { data } = await usersApi.toggleFavorite({ favorable_id: Number(id), favorable_type: 'App\\Models\\Job' });
    setIsFavorite(data.is_favorite);
  };

  if (loading) {
    return (
      <div className="max-w-xl mx-auto px-4 py-8 animate-pulse space-y-4">
        <div className="h-52 bg-surface-dim rounded-2xl" />
        <div className="h-6 w-2/3 bg-surface-dim rounded" />
        <div className="h-24 bg-surface-dim rounded-2xl" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <h2 className="text-xl font-semibold text-onSurface">Trabajo no encontrado</h2>
        <Link to="/worker/jobs" className="text-primary-600 mt-2 inline-block">Volver a trabajos</Link>
      </div>
    );
  }

  const { job, client, stats, reviews, other_jobs } = data;
  const isClient = user?.id === job.client_id;
  const canChat = isAuthenticated && !isClient;

  return (
    <div className="bg-surface-dim min-h-full pb-28">
      <div className="max-w-xl mx-auto">
        {/* Hero */}
        <div className="relative h-[200px] bg-gradient-to-br from-cream2 to-cream overflow-hidden">
          <div className="absolute top-3 left-3 right-3 flex items-center justify-between z-10">
            <IconButton icon="arrow_back" onClick={() => navigate(-1)} className="bg-surface/80 backdrop-blur" />
            {isAuthenticated && (
              <IconButton
                icon={isFavorite ? 'bookmark' : 'bookmark_border'}
                onClick={handleToggleFavorite}
                className={`bg-surface/80 backdrop-blur ${isFavorite ? 'text-primary-600' : ''}`}
              />
            )}
          </div>

          {job.images && job.images.length > 0 ? (
            <img src={storageUrl(job.images[0])} alt={job.title} className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-end justify-center pb-6">
              <div className="w-[88px] h-[88px] rounded-[24px] bg-surface shadow-lg flex items-center justify-center">
                <CategoryIcon name={job.category?.icon} size={44} className="text-primary-700" />
              </div>
            </div>
          )}
        </div>

        <div className="px-4 pt-5">
          <div className="flex items-center gap-2 mb-2.5">
            <StateBadge status={job.status} />
            {job.is_urgent && <StateBadge status="urgent" />}
            <span className="text-xs text-onSurface-mute">
              {job.category?.name} · {formatRelativeTime(job.created_at)}
            </span>
          </div>
          <h1 className="text-[22px] font-extrabold tracking-tight text-onSurface leading-tight mb-5">{job.title}</h1>

          {/* Stats row */}
          <div className="flex bg-cream2 rounded-2xl py-3.5 px-1 mb-6">
            {[
              { label: 'Presupuesto', value: job.budget_range, color: 'text-primary-700' },
              { label: 'Postulaciones', value: stats?.proposals_count ?? 0, sub: 'recibidas' },
              { label: job.city ? 'Ubicación' : 'Modalidad', value: job.city || (job.is_remote ? 'Remoto' : 'Presencial') },
            ].map((s, i) => (
              <React.Fragment key={s.label}>
                {i > 0 && <div className="w-px bg-outlineVarWarm my-1" />}
                <div className="flex-1 text-center">
                  <p className="text-[11px] font-semibold text-onSurface-mute uppercase tracking-wide mb-0.5">{s.label}</p>
                  <p className={`text-[15px] font-bold ${s.color || 'text-onSurface'}`}>{s.value}</p>
                  {'sub' in s && s.sub && <p className="text-[10px] text-onSurface-mute mt-0.5">{s.sub}</p>}
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Publicado por */}
          <h3 className="text-[13px] font-bold text-onSurface-var uppercase tracking-wide mb-2.5">Publicado por</h3>
          <div className="flex items-center gap-3 p-3 bg-surface border border-outlineVarWarm rounded-2xl mb-6">
            <Link to={`/users/${client?.id}`}>
              <Avatar src={client?.avatar} name={client?.full_name} size="lg" />
            </Link>
            <div className="flex-1 min-w-0">
              <Link to={`/users/${client?.id}`} className="font-bold text-onSurface">
                {client?.full_name}
              </Link>
              <div className="flex items-center gap-2.5 text-xs text-onSurface-mute mt-0.5">
                <span className="inline-flex items-center gap-0.5">
                  <MaterialIcon name="star" size={13} fill className="text-warning-500" />
                  {(Number(client?.rating_average) || 0).toFixed(1)}
                </span>
                <span>{client?.published_jobs_count || 0} trabajos publicados</span>
              </div>
            </div>
            {canChat && (
              <Link to={`/messages/new?recipientId=${client?.id}&jobId=${job.id}`}>
                <IconButton icon="chat_bubble" tone="filled" size={40} />
              </Link>
            )}
          </div>

          {/* Descripción */}
          <h3 className="text-[13px] font-bold text-onSurface-var uppercase tracking-wide mb-2.5">Descripción</h3>
          <p className="text-sm text-onSurface leading-relaxed whitespace-pre-wrap mb-6">{job.description}</p>

          {job.skills && job.skills.length > 0 && (
            <>
              <h3 className="text-[13px] font-bold text-onSurface-var uppercase tracking-wide mb-2.5">Habilidades requeridas</h3>
              <div className="flex flex-wrap gap-2 mb-6">
                {job.skills.map((skill: any) => (
                  <span key={skill.id} className="px-3 py-1.5 bg-surface border border-outlineVarWarm text-onSurface text-sm rounded-lg">
                    {skill.name}
                  </span>
                ))}
              </div>
            </>
          )}

          {/* Ubicación (mapa decorativo) */}
          {job.city && (
            <>
              <h3 className="text-[13px] font-bold text-onSurface-var uppercase tracking-wide mb-2.5">Ubicación</h3>
              <div className="h-[120px] rounded-2xl overflow-hidden border border-outlineVarWarm bg-cream2 relative mb-2">
                <svg width="100%" height="100%" viewBox="0 0 320 120" preserveAspectRatio="none">
                  <defs>
                    <pattern id="streetGrid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M40 0H0V40" fill="none" stroke="#e6dccb" strokeWidth="1" />
                    </pattern>
                  </defs>
                  <rect width="320" height="120" fill="#fbf2e4" />
                  <rect width="320" height="120" fill="url(#streetGrid)" />
                  <path d="M0 60 L320 60" stroke="#cbbfae" strokeWidth="3" opacity="0.6" />
                  <path d="M140 0 L140 120" stroke="#cbbfae" strokeWidth="3" opacity="0.6" />
                  <g transform="translate(160 60)">
                    <circle r="22" fill="#FE904D" opacity="0.25" />
                    <circle r="11" fill="#FE904D" />
                    <circle r="4" fill="#fff" />
                  </g>
                </svg>
              </div>
              <p className="text-[13px] text-onSurface-var flex items-center gap-1 mb-6">
                <MaterialIcon name="location_on" size={14} className="text-primary-700" />
                {job.city}
                {job.state ? `, ${job.state}` : ''}
              </p>
            </>
          )}

          {reviews && reviews.length > 0 && (
            <>
              <h3 className="text-[13px] font-bold text-onSurface-var uppercase tracking-wide mb-2.5">Calificaciones de freelancers</h3>
              <div className="space-y-3 mb-6">
                {reviews.map((review: any) => (
                  <div key={review.id} className="flex items-start gap-3 bg-surface border border-outlineVarWarm rounded-2xl p-3.5">
                    <Avatar src={review.reviewer?.avatar} name={review.reviewer?.full_name} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-onSurface">{review.reviewer?.full_name}</span>
                        <span className="inline-flex items-center gap-0.5 text-xs text-onSurface-mute">
                          <MaterialIcon name="star" size={12} fill className="text-warning-500" />
                          {review.rating}
                        </span>
                      </div>
                      {review.comment && <p className="text-sm text-onSurface-var mt-1">{review.comment}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {other_jobs && other_jobs.length > 0 && (
            <>
              <h3 className="text-[13px] font-bold text-onSurface-var uppercase tracking-wide mb-2.5">
                Otros proyectos de {client?.first_name}
              </h3>
              <div className="space-y-2">
                {other_jobs.map((otherJob: any) => (
                  <Link
                    key={otherJob.id}
                    to={`/jobs/${otherJob.id}`}
                    className="flex items-center justify-between p-3 bg-surface border border-outlineVarWarm rounded-xl hover:border-primary-300 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-onSurface truncate">{otherJob.title}</p>
                      <p className="text-xs text-onSurface-mute mt-0.5">{otherJob.proposals_count} propuestas</p>
                    </div>
                    <MaterialIcon name="chevron_right" size={18} className="text-onSurface-mute shrink-0" />
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* CTA sticky */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-surface border-t border-outlineVarWarm px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex items-center gap-3 lg:left-60">
        <JobDetailCta job={job} isClient={isClient} isAuthenticated={isAuthenticated} userType={user?.user_type} />
      </div>
    </div>
  );
};

interface JobDetailCtaProps {
  job: any;
  isClient: boolean;
  isAuthenticated: boolean;
  userType?: string;
}

// Calca el bottom bar de ScreenJobDetail (Claude Design)
const JobDetailCta: React.FC<JobDetailCtaProps> = ({ job, isClient, isAuthenticated, userType }) => {
  if (!isAuthenticated) {
    return (
      <Link to="/login" className="w-full">
        <Button variant="filled" pill size="xl" className="w-full justify-center">
          Iniciá sesión para postularte
        </Button>
      </Link>
    );
  }

  if (isClient) {
    return (
      <Link to={`/jobs/${job.id}/proposals`} className="w-full">
        <Button variant="filled" pill size="xl" className="w-full justify-center">
          <MaterialIcon name="group" size={18} /> Ver propuestas
        </Button>
      </Link>
    );
  }

  if (userType === 'worker') {
    if (job.status !== 'open') {
      return (
        <Button variant="outlined" pill size="xl" className="w-full justify-center" disabled>
          Trabajo {job.status === 'in_progress' ? 'en progreso' : job.status === 'completed' ? 'completado' : 'cancelado'}
        </Button>
      );
    }
    return (
      <Link to={`/jobs/${job.id}/propose`} className="w-full">
        <Button variant="filled" pill size="xl" className="w-full justify-center">
          <MaterialIcon name="send" size={18} /> Postular propuesta
        </Button>
      </Link>
    );
  }

  return null;
};
